import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Calculator, Search, X, CheckSquare, Square, Users } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatCurrency } from '../../lib/utils';
import api from '../../api/clients';
import { installmentSchema } from '../../lib/validation';
import { useProducts } from '../../hooks/useProducts';
import { useClients } from '../../hooks/useClients';
import { useDebounce } from '../../hooks/useDebounce';

type InstallmentFormData = {
  clienteNombre: string;
  clienteDni: string;
  clienteTelefono: string;
  clienteEmail: string;
  clienteDireccion: string;
  clienteLocalidad: string;
  clienteProvincia: string;
  clienteCp: string;
  productoId?: number;
  productoNombre: string;
  productoDescripcion: string;
  productoPrecioTotal: number;
  cantidadCuotas: number;
  fechaInicio: string;
  frecuenciaPago: 'semanal' | 'quincenal' | 'mensual';
  intereses: number;
  costoEnvio: number;
  anticipoMonto: number;
  observaciones: string;
};

export function InstallmentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: contrato, isLoading } = useQuery({
    queryKey: ['installment', id],
    queryFn: () => api.get(`/installments/${id}`).then(res => res.data.data),
    enabled: isEditing,
  });

  const { data: clientsData } = useClients({ limit: 1000 });
  const clients = clientsData?.data ?? [];
  const queryClient = useQueryClient();

  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [autocompletar, setAutocompletar] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clienteDropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const shouldSearchProducts =
    searchTerm.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchTerm);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearchTerm.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  const [calculos, setCalculos] = useState({
    totalConInteres: 0,
    montoCuota: 0,
    anticipoMonto: 0,
    saldoFinanciar: 0,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InstallmentFormData>({
    resolver: zodResolver(installmentSchema),
    defaultValues: {
      clienteNombre: '',
      clienteDni: '',
      clienteTelefono: '',
      clienteEmail: '',
      clienteDireccion: '',
      clienteLocalidad: '',
      clienteProvincia: '',
      clienteCp: '',
      productoId: undefined,
      productoNombre: '',
      productoDescripcion: '',
      productoPrecioTotal: 0,
      frecuenciaPago: 'mensual',
      intereses: 0,
      costoEnvio: 0,
      anticipoMonto: 0,
      cantidadCuotas: 12,
      fechaInicio: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
      observaciones: '',
    },
  });

  useEffect(() => {
    if (contrato && isEditing) {
      setValue('clienteNombre', contrato.clienteNombre);
      setValue('clienteDni', contrato.clienteDni);
      setValue('clienteTelefono', contrato.clienteTelefono || '');
      setValue('clienteEmail', contrato.clienteEmail || '');
      setValue('clienteDireccion', contrato.clienteDireccion);
      setValue('clienteLocalidad', contrato.clienteLocalidad);
      setValue('clienteProvincia', contrato.clienteProvincia);
      setValue('clienteCp', contrato.clienteCp || '');
      setValue('productoId', contrato.productoId || undefined);
      setValue('productoNombre', contrato.productoNombre);
      setValue('productoDescripcion', contrato.productoDescripcion || '');
      setValue('productoPrecioTotal', Number(contrato.productoPrecioTotal));
      setValue('cantidadCuotas', contrato.cantidadCuotas);
      setValue('fechaInicio', contrato.fechaInicio.split('T')[0]);
      setValue('frecuenciaPago', contrato.frecuenciaPago || 'mensual');
      setValue('intereses', Number(contrato.intereses || 0));
      setValue('costoEnvio', Number(contrato.costoEnvio || 0));
      setValue('anticipoMonto', Number(contrato.anticipoMonto || 0));
      setValue('observaciones', contrato.observaciones || '');

      if (contrato.producto) {
        setSelectedProduct(contrato.producto);
        setSearchTerm(contrato.producto.nombre);
      }

      if (contrato.clienteNombre) {
        const cliente = clients.find((c: any) => c.nombre === contrato.clienteNombre);
        if (cliente) {
          setClienteSeleccionado(cliente);
          setSearchCliente(cliente.nombre);
        }
      }
    }
  }, [contrato, isEditing, setValue, clients]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = clients.filter((c: any) => {
    const searchLower = searchCliente.toLowerCase();
    const nombreMatch = c.nombre?.toLowerCase().includes(searchLower) || false;
    const telefonoMatch = c.telefono?.toLowerCase().includes(searchLower) || false;
    return nombreMatch || telefonoMatch;
  }) || [];

  const handleSelectClient = (client: any) => {
    setClienteSeleccionado(client);
    setSearchCliente(client.nombre);
    setShowClienteDropdown(false);

    setValue('clienteNombre', client.nombre);
    setValue('clienteDni', client.cuil || client.documento || '');
    setValue('clienteTelefono', client.telefono || '');
    setValue('clienteEmail', client.email || '');
    setValue('clienteDireccion', client.direccion || '');
    setValue('clienteLocalidad', client.localidad || '');
    setValue('clienteProvincia', client.provincia || '');
    setValue('clienteCp', client.codigoPostal || client.cp || '');

    toast.success(`Cliente seleccionado: ${client.nombre}`);
  };

  const toggleAutocompletar = () => {
    const nuevoEstado = !autocompletar;
    setAutocompletar(nuevoEstado);

    if (nuevoEstado && clienteSeleccionado) {
      setValue('clienteDni', clienteSeleccionado.cuil || clienteSeleccionado.documento || '');
      setValue('clienteTelefono', clienteSeleccionado.telefono || '');
      setValue('clienteEmail', clienteSeleccionado.email || '');
      setValue('clienteDireccion', clienteSeleccionado.direccion || '');
      setValue('clienteLocalidad', clienteSeleccionado.localidad || '');
      setValue('clienteProvincia', clienteSeleccionado.provincia || '');
      setValue('clienteCp', clienteSeleccionado.codigoPostal || clienteSeleccionado.cp || '');
    }
  };

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);
    setSearchTerm(product.nombre);
    setValue('productoId', product.id);
    setValue('productoNombre', product.nombre);
    setValue('productoDescripcion', product.descripcion || '');
    setValue('productoPrecioTotal', Number(product.precio || 0));
    setShowDropdown(false);
    toast.success('Producto agregado');
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setValue('productoId', undefined);
    setValue('productoNombre', '');
    setValue('productoDescripcion', '');
    setValue('productoPrecioTotal', 0);
  };

  const watchFields = watch([
    'productoPrecioTotal',
    'cantidadCuotas',
    'intereses',
    'costoEnvio',
    'anticipoMonto',
  ]);

  useEffect(() => {
    const [precioTotal, cantidadCuotas, intereses, costoEnvio, anticipoMonto] = watchFields;
    const montoIntereses = (precioTotal || 0) * ((intereses || 0) / 100);
    const totalConInteres = (precioTotal || 0) + montoIntereses + (costoEnvio || 0);
    const montoCuota = cantidadCuotas > 0 ? (totalConInteres / cantidadCuotas) : 0;
    const anticipo = anticipoMonto || 0;
    const saldoFinanciar = Math.max(0, totalConInteres - anticipo);

    setCalculos({
      totalConInteres,
      montoCuota,
      anticipoMonto: anticipo,
      saldoFinanciar,
    });
  }, watchFields);

  const handleClearClient = () => {
    setClienteSeleccionado(null);
    setSearchCliente('');
    setValue('clienteNombre', '');
    setValue('clienteDni', '');
    setValue('clienteTelefono', '');
    setValue('clienteEmail', '');
    setValue('clienteDireccion', '');
    setValue('clienteLocalidad', '');
    setValue('clienteProvincia', '');
    setValue('clienteCp', '');
  };

  const isFieldDisabled = !!clienteSeleccionado && autocompletar;

  const mutation = useMutation({
    mutationFn: (data: InstallmentFormData) => {
      const payload = {
        ...data,
        productoPrecioTotal: Number(data.productoPrecioTotal),
        cantidadCuotas: Number(data.cantidadCuotas),
        intereses: Number(data.intereses || 0),
        costoEnvio: Number(data.costoEnvio || 0),
        anticipoMonto: Number(data.anticipoMonto || 0),
        fechaInicio: data.fechaInicio,
      };
      const endpoint = isEditing ? `/installments/${id}` : '/installments';
      const method = isEditing ? 'PUT' : 'POST';
      return api({ method, url: endpoint, data: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installments'] });
      if (isEditing && id) {
        queryClient.invalidateQueries({ queryKey: ['installments', parseInt(id)] });
      }
      toast.success(isEditing ? 'Contrato actualizado exitosamente' : 'Contrato creado exitosamente');
      navigate('/cuotas');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al guardar el contrato');
    },
  });

  const onSubmit = (data: InstallmentFormData) => {
    mutation.mutate(data);
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando contrato...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/cuotas')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Contrato' : 'Nuevo Contrato en Cuotas'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar Cliente Existente
            </label>

            <div className="relative" ref={clienteDropdownRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar cliente por nombre o teléfono..."
                value={searchCliente}
                onChange={(e) => {
                  setSearchCliente(e.target.value);
                  setShowClienteDropdown(true);
                  if (!e.target.value) {
                    handleClearClient();
                  }
                }}
                onFocus={() => setShowClienteDropdown(true)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchCliente && (
                <button
                  type="button"
                  onClick={handleClearClient}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}

              {showClienteDropdown && searchCliente && filteredClients.length > 0 && (
                <div className="absolute top-full left-0 z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredClients.map((client: any) => (
                    <button
                      key={client.id}
                      type="button"
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                      onClick={() => handleSelectClient(client)}
                    >
                      <div>
                        <p className="font-medium text-gray-800">{client.nombre}</p>
                        {client.telefono && (
                          <p className="text-xs text-gray-400">{client.telefono}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {clienteSeleccionado && (
            <div className="md:col-span-2">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <button
                  type="button"
                  onClick={toggleAutocompletar}
                  className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  {autocompletar ? (
                    <CheckSquare size={18} className="text-blue-600" />
                  ) : (
                    <Square size={18} className="text-blue-400" />
                  )}
                  {autocompletar ? 'Autocompletar datos activado' : 'Autocompletar datos desactivado'}
                </button>
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
            <Input
              {...register('clienteNombre')}
              placeholder="Ej: Juan Pérez"
              error={errors.clienteNombre?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI *</label>
            <Input
              {...register('clienteDni')}
              placeholder="Ej: 12345678"
              error={errors.clienteDni?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <Input
              {...register('clienteTelefono')}
              placeholder="Ej: 11 1234-5678"
              error={errors.clienteTelefono?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              {...register('clienteEmail')}
              placeholder="Ej: juan@email.com"
              error={errors.clienteEmail?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección *</label>
            <Input
              {...register('clienteDireccion')}
              placeholder="Ej: Av. Tecnología 123, Oficina 4B"
              error={errors.clienteDireccion?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Localidad *</label>
            <Input
              {...register('clienteLocalidad')}
              placeholder="Ej: San Andrés de Giles"
              error={errors.clienteLocalidad?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provincia *</label>
            <Input
              {...register('clienteProvincia')}
              placeholder="Ej: Buenos Aires"
              error={errors.clienteProvincia?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal</label>
            <Input
              {...register('clienteCp')}
              placeholder="Ej: 6720"
              error={errors.clienteCp?.message}
              disabled={isFieldDisabled}
            />
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Datos del Producto</h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar Producto
            </label>
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Buscar producto por nombre..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                      setSelectedProduct(null);
                      setValue('productoId', undefined);
                    }
                    if (!e.target.value) {
                      handleClearProduct();
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="pl-10 pr-10"
                />
                {selectedProduct && (
                  <button
                    type="button"
                    onClick={handleClearProduct}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {showDropdown && searchTerm && filteredProducts.length > 0 && (
                <div
                  className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl"
                  style={{
                    maxHeight: '200px',
                    height: 'auto',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth'
                  }}
                >
                  {filteredProducts.map((product: any) => {
                    const truncateDescription = (text: string) => {
                      if (!text) return '';
                      if (text.length <= 60) return text;
                      return text.substring(0, 60) + '.....';
                    };

                    return (
                      <button
                        key={product.id}
                        type="button"
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className="flex-1 min-w-0 pr-3">
                          <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                          {product.descripcion && (
                            <p className="text-xs text-gray-400 truncate">
                              {truncateDescription(product.descripcion)}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-blue-600 shrink-0">
                          {formatCurrency(product.precio)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {showDropdown && searchTerm && loadingProducts && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                  Buscando productos...
                </div>
              )}

              {showDropdown && searchTerm && !loadingProducts && filteredProducts.length === 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                  No se encontraron productos
                </div>
              )}
            </div>
          </div>

          {selectedProduct && (
            <div className="md:col-span-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">{selectedProduct.nombre}</p>
                  <p className="text-sm text-gray-600">{selectedProduct.descripcion}</p>
                  <p className="text-sm font-semibold text-blue-600">
                    Precio: {formatCurrency(selectedProduct.precio)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClearProduct}
                  className="p-1 text-gray-400 hover:text-red-500 rounded"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Producto *</label>
              <Input
                {...register('productoNombre')}
                placeholder="Nombre del producto"
                error={errors.productoNombre?.message}
                disabled={!!selectedProduct}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio Total *</label>
              <Input
                type="number"
                step="0.01"
                {...register('productoPrecioTotal', { valueAsNumber: true })}
                placeholder="0.00"
                error={errors.productoPrecioTotal?.message}
                disabled={!!selectedProduct}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <Input
                {...register('productoDescripcion')}
                placeholder="Descripción breve"
                error={errors.productoDescripcion?.message}
                disabled={!!selectedProduct}
              />
            </div>
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Condiciones de Pago</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad de Cuotas *</label>
            <Input
              type="number"
              {...register('cantidadCuotas', { valueAsNumber: true })}
              placeholder="12"
              error={errors.cantidadCuotas?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio *</label>
            <Input
              type="date"
              {...register('fechaInicio')}
              error={errors.fechaInicio?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia de Pago</label>
            <select
              {...register('frecuenciaPago')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="semanal">Semanal</option>
              <option value="quincenal">Quincenal</option>
              <option value="mensual">Mensual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Interés (%)</label>
            <Input
              type="number"
              step="0.01"
              {...register('intereses', { valueAsNumber: true })}
              placeholder="0"
              error={errors.intereses?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío</label>
            <Input
              type="number"
              step="0.01"
              {...register('costoEnvio', { valueAsNumber: true })}
              placeholder="0.00"
              error={errors.costoEnvio?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Anticipo ($ ARS)</label>
            <Input
              type="number"
              step="0.01"
              {...register('anticipoMonto', { valueAsNumber: true })}
              placeholder="Ej: 65200"
              error={errors.anticipoMonto?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              {...register('observaciones')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observaciones adicionales..."
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={18} className="text-blue-600" />
            <h4 className="font-semibold text-blue-700">Resumen de Cálculos</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total con interés</span>
              <p className="font-semibold text-gray-800">{formatCurrency(calculos.totalConInteres)}</p>
            </div>
            <div>
              <span className="text-gray-500">Monto por cuota</span>
              <p className="font-semibold text-gray-800">{formatCurrency(calculos.montoCuota)}</p>
            </div>
            <div>
              <span className="text-gray-500">Anticipo (Cuota 1)</span>
              <p className="font-semibold text-gray-800">{formatCurrency(calculos.anticipoMonto)}</p>
            </div>
            <div>
              <span className="text-gray-500">Saldo a financiar</span>
              <p className="font-semibold text-gray-800">{formatCurrency(calculos.saldoFinanciar)}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button type="submit" disabled={isSubmitting}>
            <Save size={18} />
            {isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar Contrato' : 'Crear Contrato'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cuotas')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}